<template>
  <div class="phrase-content">
    <el-dialog
      :visible.sync="dialogVisible"
      custom-class="phrase-dialog"
      :show-close="false"
      :modal="false"
      v-model="activeTab"
      @close="closeDialog"
      @open="openDialog"
    >
      <el-tabs v-model="activeTab" :stretch="true">
        <el-tab-pane
          v-for="category in phrasesItems"
          :key="category.category_id"
          :name="`category${category.category_id}`"
        >
          <span slot="label">
            <img :src="usefulPhraseIcons(category.category_id)" />
            <span class="label">{{ category.description }}</span>
          </span>
          <perfect-scrollbar class="scroll-bar" v-if="category.phrases.length">
            <el-row v-for="phrase in category.phrases" :key="phrase.phrase_id">
              <el-col :span="20">
                <span class="items item-en">{{ phrase.phrase_en }}</span>
                <span class="items item-ja">{{ phrase.phrase_ja }}</span>
              </el-col>
              <el-col :span="4">
                <div class="item-btn" @click="phraseMessage(phrase)">
                  <span>{{ $t('usefulPhrases.buttonLabel') }}</span>
                </div>
              </el-col>
            </el-row>
          </perfect-scrollbar>
        </el-tab-pane>
      </el-tabs>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import logger from '@/modules/Logger';
import { getApiMethod } from '@/modules/apiSelector';
import { apolloClient } from '@/modules/myStageGraphQL';
import { SALES_TRIAL_USEFUL_PHRASES } from '@/graphql/salesTrial';
import { mystageServiceIdHeaderName } from '@/utils/constants';

export default {
  data() {
    return {
      dialogVisible: false,
      activeTab: 'category1',
      tempPhrasesItems: null
    };
  },
  mounted() {
    if (this.isAssessmentLesson) {
      this.activeTab = 'category5';
    }

    if (!this.isStudent) {
      this.activeTab = 'category6';
    }
    this.fetchPhrases();
  },
  computed: {
    ...mapState('layout', ['isVideoFullScreen']),
    ...mapState(['isTrial', 'lessonToken']),
    ...mapGetters(['isStudent', 'isLessonEnded', 'jwtKeyValue', 'isAssessmentLesson']),
    ...mapGetters('uaParser', ['isSafariBrowser']),

    phrasesItems() {
      if (!this.tempPhrasesItems) {
        return;
      }
      let items = this.tempPhrasesItems;

      // If the user is a trainer, swap the order of the English and Japanese phrases.
      if (!this.isStudent) {
        items = items.map((category) => {
          category.phrases = category.phrases.map((phrase) => ({
            phrase_id: phrase.phrase_id,
            phrase_ja: phrase.phrase_en,
            phrase_en: phrase.phrase_ja
          }));
          return category;
        });
      }
      return items;
    },
    quill() {
      return this.$parent.$refs.editor.quill;
    }
  },
  watch: {
    isVideoFullScreen: {
      handler: function (value) {
        if (value && this.dialogVisible) {
          this.dialogVisible = !value;
        }
      }
    }
  },
  methods: {
    async fetchPhrases() {
      if (this.isTrial) {
        try {
          const { data } = await apolloClient.query({
            query: SALES_TRIAL_USEFUL_PHRASES,
            variables: {
              token: this.lessonToken
            },
            context: {
              headers: {
                [mystageServiceIdHeaderName]: this.$route.params.serviceId
              },
              authRequired: false
            },
            fetchPolicy: 'no-cache'
          });
          this.tempPhrasesItems = data.salesTrialUsefulPhrases.items;
        } catch (error) {
          logger.error('[UsefulPhrases] Failed to load trial phrases: ' + error.message, {
            operation: 'usefulphrases:load-list'
          });
        }
      } else {
        const apiMethod = getApiMethod('getUsefulPhrases');
        await apiMethod(this.$route.params.lessonId, this.jwtKeyValue, this.isTrial, (data) => {
          this.tempPhrasesItems = data;
        });
      }
    },
    openDialog() {
      document.querySelector('.chatbox').classList.add('phrase-active');
      document.querySelector('.sp_bottom_menu').classList.add('phrase-active');
    },
    closeDialog() {
      this.$emit('phraseCloseDialog');
      document.querySelector('.chatbox').classList.remove('phrase-active');
      document.querySelector('.sp_bottom_menu').classList.remove('phrase-active');
    },
    phraseMessage(item) {
      this.$emit('phraseMessage', item);
      this.dialogVisible = false;
    },
    usefulPhraseIcons(categoryId) {
      const icons = [
        {
          id: 1,
          image: 'confirmation'
        },
        {
          id: 2,
          image: 'question'
        },
        {
          id: 3,
          image: 'request'
        },
        {
          id: 4,
          image: 'trouble'
        },
        {
          id: 5,
          image: 'level0'
        },
        {
          id: 6,
          image: 'technicalproblem'
        },
        {
          id: 7,
          image: 'beginner'
        }
      ];

      let phraseIcon = icons.find((icn) => icn.id == categoryId);
      return require(`@/assets/img/up-icon-${phraseIcon.image}.svg`);
    },
    quillBindings() {
      if (this.isStudent) {
        return;
      }
      let vm = this;
      this.quill.keyboard.bindings[8].unshift({
        key: 8, // backspace
        handler: (evt) => vm.quillDeleteHandler(evt)
      });

      this.quill.keyboard.bindings[46].unshift({
        key: 46, // delete
        handler: (evt) => vm.quillDeleteHandler(evt)
      });
    },
    // remove whole block of noneditable usefulphrase
    quillDeleteHandler(range) {
      let vm = this;
      if (vm.isPreviousCharacterNonEditable(range.index + 1)) {
        setTimeout(() => {
          let [line, offset] = vm.quill.getLine(range.index);
          let remainingPhrase = vm.remainingPartialPhrase(line);

          const index = vm.quill.getIndex(line);
          vm.quill.deleteText(index, remainingPhrase || offset);
        }, 100);
      }
      return true;
    },
    isPreviousCharacterNonEditable(length) {
      const previousChar = this.quill.getFormat(length - 2, 1);
      return previousChar['phrase-noneditable'];
    },
    // [Safari] Remove the remaining text when user deleted a partial text on useful phrase
    remainingPartialPhrase(line) {
      if (!this.isSafariBrowser || !line.next) {
        return 0;
      }
      const blockPhrase = line.next.prev && line.next.prev.cache.delta.ops[0];
      return blockPhrase.attributes['phrase-noneditable'] ? blockPhrase.insert.length + 1 : 0;
    }
  }
};
</script>
<style scoped src="@/assets/css/pc/phrases.css"></style>
<style scoped src="@/assets/css/sp/phrases.css"></style>
